package org.teddapp.project.parcours

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Parcours_step
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.ProgressService
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.nowMillis

/** Ce qu'un import a écrit, pour l'annoncer à l'écran et le déclarer au noyau. */
data class ParcoursImportResult(
    val skillId: String,
    val skillCreated: Boolean,
    val steps: Int,
    val objectives: Int,
    val tasks: Int,
    val micros: Int,
    val projects: Int,
    val actions: Int,
    /** Les étapes avec les identifiants de leurs feuilles, telles qu'écrites en base. */
    val declaration: List<DeclaredStep> = emptyList(),
    /** Cible d'heures de la compétence après l'import : celle du parcours, ou celle qu'elle gardait. */
    val targetHours: Long = 1000L,
)

/** Une étape écrite, avec ses feuilles identifiées : ce que le noyau doit connaître. */
data class DeclaredStep(
    val code: String,
    val title: String,
    val isProject: Boolean,
    val level: String?,
    val leaves: List<DeclaredLeaf>,
)

data class DeclaredLeaf(val id: String, val title: String, val minutes: Int, val kind: String?)

/** Lecture des étapes d'un parcours. */
class ParcoursRepository(private val db: TeddDatabase) {
    fun observeAll(): Flow<List<Parcours_step>> =
        db.parcoursStepQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeBySkill(skillId: String): Flow<List<Parcours_step>> =
        db.parcoursStepQueries.selectBySkill(skillId).asFlow().mapToList(Dispatchers.Default)
}

/**
 * Écrit un [ParcoursPlan] dans la base : compétence, étapes, objectifs, jalons, micro,
 * organisation, projets, statuts et actions. Tout part dans une seule transaction.
 *
 * Les identifiants sont dérivés de la compétence et des codes du fichier (« M1 »,
 * « O1.1 », « T1.1.1 », « A1.1 »). Recharger le même fichier corrigé met donc à jour
 * les lignes existantes au lieu de les doubler, et ne touche ni au statut ni aux
 * minutes réelles de ce qui a déjà été fait.
 */
class ParcoursImporter(
    private val db: TeddDatabase,
    private val progress: ProgressService? = null,
) {

    /**
     * Importe le plan. Si [skillId] est donné, le parcours s'attache à cette compétence ;
     * sinon une compétence du même nom est réutilisée, ou créée.
     */
    suspend fun import(plan: ParcoursPlan, skillId: String? = null): ParcoursImportResult = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val year = TODAY_ISO.take(4).toLongOrNull()
        var created = false
        val sid = skillId
            ?: db.skillQueries.selectAll().executeAsList()
                .firstOrNull { it.name.trim().equals(plan.competence.trim(), ignoreCase = true) }?.id
            ?: run {
                created = true
                "sk_parcours_" + (plan.slug ?: slugOf(plan.competence))
            }
        val base = sid.removePrefix("sk_")
        val targetHours = ((plan.totalMinutes + 59) / 60).toLong().coerceAtLeast(1L)

        val milestoneIds = mutableListOf<String>()
        val declaration = mutableListOf<DeclaredStep>()
        var retainedTarget = targetHours
        db.transaction {
            // ── Compétence ──────────────────────────────────────────────
            val existing = db.skillQueries.selectByIdAnyState(sid).executeAsOneOrNull()
            if (existing == null) {
                val order = db.skillQueries.selectAll().executeAsList().size.toLong()
                db.skillQueries.insert(sid, plan.competence.trim(), "#22508A", "book", null, targetHours, null, null, 0L, 0L, order, now, now)
            } else {
                if (existing.is_deleted == 1L) db.skillQueries.restore(now, sid)
                // La cible suit le parcours quand elle était restée à la valeur par défaut.
                if (existing.target_hours == 1000L && targetHours != 1000L) {
                    db.skillQueries.update(
                        existing.name, existing.color, existing.icon, existing.intention, targetHours, existing.deadline,
                        existing.tags, existing.pinned, existing.archived, existing.order_index, now, sid,
                    )
                } else {
                    retainedTarget = existing.target_hours
                }
            }

            // ── Organisation qui regroupe les projets du parcours ───────
            val orgId = "org_$base"
            val org = db.organizationQueries.selectByIdAnyState(orgId).executeAsOneOrNull()
            if (org == null) {
                val order = db.organizationQueries.selectAll().executeAsList().size.toLong()
                db.organizationQueries.insert(orgId, plan.competence.trim(), "Projets du parcours", "#22508A", "grid", order, now, now)
            } else if (org.is_deleted == 1L) {
                db.organizationQueries.restore(now, orgId)
            }

            // ── Étapes ──────────────────────────────────────────────────
            plan.steps.forEachIndexed { position, step ->
                val stepId = "pst_${base}_${step.code}"
                when (step) {
                    is ParcoursModulePlan -> {
                        upsertStep(stepId, sid, step.code, "module", position.toLong(), step.title, step.summary, step.minutes, null, null, null, now)
                        val leaves = mutableListOf<DeclaredLeaf>()
                        step.objectives.forEachIndexed { oi, obj ->
                            val objId = "obj_${base}_${obj.code}"
                            upsertObjective(objId, sid, stepId, obj, year, oi.toLong(), now)
                            obj.tasks.forEachIndexed { ti, task ->
                                val msId = "ms_${base}_${task.code}"
                                upsertMilestone(msId, objId, task, ti.toLong(), now)
                                milestoneIds += msId
                                task.micros.forEachIndexed { mi, micro ->
                                    val microId = "t_${base}_${task.code}_${mi + 1}"
                                    leaves += DeclaredLeaf(microId, micro.title, micro.minutes, micro.kind)
                                    upsertTask(
                                        id = microId,
                                        parent = ParentKind.Milestone, parentId = msId, statusId = null,
                                        title = micro.title, description = null, minutes = micro.minutes,
                                        kind = micro.kind, deliverable = null, acceptance = null,
                                        order = mi.toLong(), now = now,
                                    )
                                }
                            }
                        }
                        declaration += DeclaredStep(step.code, step.title, isProject = false, level = null, leaves = leaves)
                    }
                    is ParcoursProjectPlan -> {
                        val projId = "proj_${base}_${step.code}"
                        upsertProject(projId, orgId, sid, step, position.toLong(), now)
                        val todoId = ensureStatuses(projId, base, step.code, now)
                        upsertStep(stepId, sid, step.code, "projet", position.toLong(), step.title, step.context, step.minutes, step.levelName, step.levelDesc, projId, now)
                        val leaves = mutableListOf<DeclaredLeaf>()
                        step.actions.forEachIndexed { ai, action ->
                            val actionId = "t_${base}_${action.code}"
                            leaves += DeclaredLeaf(actionId, action.title, action.minutes, null)
                            upsertTask(
                                id = actionId,
                                parent = ParentKind.Project, parentId = projId, statusId = todoId,
                                title = action.title, description = null, minutes = action.minutes,
                                kind = null, deliverable = action.deliverable, acceptance = action.acceptance,
                                order = ai.toLong(), now = now,
                            )
                        }
                        declaration += DeclaredStep(step.code, step.title, isProject = true, level = step.levelName, leaves = leaves)
                    }
                }
            }
        }

        // La fiche elle-même devient une ressource texte de la compétence : l'assistant
        // la lit comme un document. Rechargée, elle est réécrite et réindexée.
        db.transaction { upsertSheet(sid, base, plan, now) }

        // La progression se recalcule depuis les micro : un rechargement garde ce qui est fait.
        if (progress != null) for (id in milestoneIds) progress.recalcMilestone(id)

        ParcoursImportResult(
            skillId = sid,
            skillCreated = created,
            steps = plan.steps.size,
            objectives = plan.objectiveCount,
            tasks = plan.taskCount,
            micros = plan.microCount,
            projects = plan.projects.size,
            actions = plan.actionCount,
            declaration = declaration,
            targetHours = retainedTarget,
        )
    }

    private fun upsertSheet(skillId: String, base: String, plan: ParcoursPlan, now: Long) {
        val id = "res_parcours_$base"
        val title = ParcoursMarkdown.title(plan)
        val body = ParcoursMarkdown.render(plan)
        val existing = db.skillResourceQueries.selectByIdAnyState(id).executeAsOneOrNull()
        if (existing == null) {
            // En tête de la liste : c'est la ressource qui décrit la compétence.
            db.skillResourceQueries.insert(id, skillId, null, "text", "markdown", title, null, body, 0L, now, now)
        } else {
            if (existing.is_deleted == 1L) db.skillResourceQueries.restore(now, id)
            if (existing.title != title || existing.body != body || existing.format != "markdown") {
                db.skillResourceQueries.updateText(title, body, "markdown", now, id)
            }
        }
    }

    private fun upsertStep(
        id: String, skillId: String, code: String, kind: String, position: Long, title: String, summary: String?,
        minutes: Int, levelName: String?, levelDesc: String?, projectId: String?, now: Long,
    ) {
        val q = db.parcoursStepQueries
        val existing = q.selectByIdAnyState(id).executeAsOneOrNull()
        if (existing == null) {
            q.insert(id, skillId, code, kind, position, title, summary, minutes.toLong(), levelName, levelDesc, projectId, now, now)
        } else {
            q.update(skillId, code, kind, position, title, summary, minutes.toLong(), levelName, levelDesc, projectId, now, id)
            if (existing.is_deleted == 1L) q.restore(now, id)
        }
    }

    private fun upsertObjective(id: String, skillId: String, stepId: String, obj: ParcoursObjectivePlan, year: Long?, order: Long, now: Long) {
        val q = db.objectiveQueries
        val existing = q.selectByIdAnyState(id).executeAsOneOrNull()
        val description = obj.outcome
        if (existing == null) {
            q.insert(id, null, obj.title, description, null, null, year, "active", 0L, now, now)
        } else {
            q.update(existing.category_id, obj.title, description, existing.motivation, existing.deadline, existing.year, existing.status, existing.progress, now, id)
            if (existing.is_deleted == 1L) q.restore(now, id)
        }
        q.setSkillAndStep(skillId, stepId, id)
    }

    private fun upsertMilestone(id: String, objectiveId: String, task: ParcoursTaskPlan, order: Long, now: Long) {
        val q = db.milestoneQueries
        val existing = q.selectByIdAnyState(id).executeAsOneOrNull()
        if (existing == null) {
            q.insert(id, objectiveId, task.title, null, null, 0L, order, now, now)
        } else {
            q.update(task.title, existing.description, existing.deadline, existing.progress, order, now, id)
            if (existing.is_deleted == 1L) q.restore(now, id)
        }
    }

    private fun upsertTask(
        id: String, parent: ParentKind, parentId: String, statusId: String?, title: String, description: String?,
        minutes: Int, kind: String?, deliverable: String?, acceptance: String?, order: Long, now: Long,
    ) {
        val q = db.taskQueries
        val existing = q.selectByIdAnyState(id).executeAsOneOrNull()
        if (existing == null) {
            q.insert(id, parent.raw, parentId, statusId, title, description, "pending", null, null, null, minutes.toLong(), null, null, null, order, null, now, now)
        } else {
            q.update(
                parent.raw, parentId, existing.status_id ?: statusId, title, description ?: existing.description,
                existing.status, existing.priority, existing.effort, existing.due_date, minutes.toLong(),
                existing.real_minutes, existing.recurrence, existing.pomodoro_pool_date, order, existing.completed_at, now, id,
            )
            if (existing.is_deleted == 1L) q.restore(now, id)
        }
        q.setParcoursFields(kind, deliverable, acceptance, id)
    }

    private fun upsertProject(id: String, orgId: String, skillId: String, step: ParcoursProjectPlan, order: Long, now: Long) {
        val q = db.projectQueries
        val existing = q.selectByIdAnyState(id).executeAsOneOrNull()
        val livrables = step.deliverables.takeIf { it.isNotEmpty() }?.joinToString("\n") { "• $it" }
        if (existing == null) {
            q.insert(id, orgId, null, step.title, step.context, livrables, "#22508A", "flag", order, now, now)
        } else {
            q.update(orgId, existing.objective_id, step.title, step.context, livrables, existing.color, existing.icon, order, now, id)
            if (existing.is_deleted == 1L) q.restore(now, id)
        }
        q.setSkill(skillId, id)
    }

    /** Les actions ne s'affichent que dans une colonne : on pose les trois statuts usuels. Retourne « À faire ». */
    private fun ensureStatuses(projectId: String, base: String, code: String, now: Long): String {
        val q = db.projectStatusQueries
        val defs = listOf(
            Triple("todo", "À faire", "#6366F1"),
            Triple("doing", "En cours", "#22D3EE"),
            Triple("done", "Terminé", "#10B981"),
        )
        defs.forEachIndexed { i, (key, name, color) ->
            val id = "st_${base}_${code}_$key"
            val existing = q.selectByIdAnyState(id).executeAsOneOrNull()
            if (existing == null) q.insert(id, projectId, name, color, i.toLong(), if (key == "done") 1L else 0L, now, now)
            else if (existing.is_deleted == 1L) q.restore(now, id)
        }
        return "st_${base}_${code}_todo"
    }

    companion object {
        /** « Prendre la parole et écrire » → « prendre-la-parole-et-ecrire », borné à 48 signes. */
        fun slugOf(name: String): String {
            val ascii = name.lowercase().map { c ->
                when (c) {
                    'à', 'â', 'ä', 'á' -> 'a'; 'é', 'è', 'ê', 'ë' -> 'e'; 'î', 'ï', 'í' -> 'i'
                    'ô', 'ö', 'ó' -> 'o'; 'ù', 'û', 'ü', 'ú' -> 'u'; 'ç' -> 'c'; 'ñ' -> 'n'
                    else -> c
                }
            }.joinToString("")
            return ascii.replace(Regex("[^a-z0-9]+"), "-").trim('-').take(48).ifEmpty { "parcours" }
        }
    }
}
